import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { SummaryCards } from './SummaryCards';
import { MonthlyChart } from './charts/MonthlyChart';
import { CategoryChart } from './charts/CategoryChart';
import { ProjectChart } from './charts/ProjectChart';
import { ProductChart } from './charts/ProductChart';
import { DetailedLineItemsChart } from './charts/DetailedLineItemsChart';
import { LineItemExplorer } from './LineItemExplorer';
import { InvoiceTable } from './InvoiceTable';
import { filterLineItemsByTimeRange, TIME_RANGES, getTimeRangeLabel } from '../utils/dataUtils';
import { toCsv, toFileSlug, todayStamp, downloadTextFile } from '../utils/downloadUtils';
import { refreshLiveRateIfStale, describeRate } from '../utils/currency';
import { useCurrency } from '../hooks/useCurrency';
import { SettingsModal } from './SettingsModal';
import { CurrencyMenu } from './CurrencyMenu';
import { ThemeToggle } from './ThemeToggle';
import { Icon } from './Icon';
import { GenerateReportModal } from './GenerateReportModal';

// Keyboard and touch friendly alternative to clicking a chart segment.
const DrillDownSelect = ({ label, data, onSelect }) => {
  const options = Object.entries(data || {})
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
  if (options.length === 0) return null;

  return (
    <select
      className="drilldown-select"
      aria-label={label}
      value=""
      onChange={(e) => { if (e.target.value) onSelect(e.target.value); }}
    >
      <option value="">View line items…</option>
      {options.map((name) => (
        <option key={name} value={name}>{name}</option>
      ))}
    </select>
  );
};

// Placeholder shapes shown while an account's data loads for the first time
const LoadingSkeleton = () => (
  <div className="skeleton-layout" aria-hidden="true">
    <div className="skeleton-row">
      <div className="skeleton skeleton-hero"></div>
      <div className="skeleton-tiles">
        <div className="skeleton"></div>
        <div className="skeleton"></div>
        <div className="skeleton"></div>
        <div className="skeleton"></div>
      </div>
    </div>
    <div className="skeleton skeleton-chart"></div>
  </div>
);

export const Dashboard = ({
  accountName,
  allInvoices,
  detailedLineItems,
  processedData,
  isLoading,
  loadProgress,
  error,
  warning,
  statusMessage,
  cacheStatus = { isCached: false },
  apiToken,
  timeRange,
  userEmail,
  headerActions,
  onLogout,
  onRefresh,
  onClearCache,
  onTimeRangeChange,
  onDismissError
}) => {
  const [drillDown, setDrillDown] = useState(null); // { type: 'category' | 'project' | 'product', value }
  const [showForecast, setShowForecast] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [customLogo, setCustomLogo] = useState(null);
  const lineItemsSectionRef = useRef(null);
  // Re-render amounts when the display currency changes
  useCurrency();

  // Load saved custom logo from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('customLogo');
      if (saved) setCustomLogo(saved);
    } catch (e) {
      console.warn('Unable to load custom logo', e);
    }
  }, []);

  useEffect(() => {
    refreshLiveRateIfStale();
  }, []);

  // A drill-down belongs to one account; leave it when the account changes
  useEffect(() => {
    setDrillDown(null);
  }, [accountName]);

  const filteredLineItems = useMemo(() => {
    if (!detailedLineItems || detailedLineItems.length === 0) return [];
    const filtered = filterLineItemsByTimeRange(detailedLineItems, timeRange);
    return Array.isArray(filtered) ? filtered : [];
  }, [detailedLineItems, timeRange]);

  const openDrillDown = useCallback((type, value) => {
    setDrillDown({ type, value });
    // The line items section is further down the page than the charts that open it
    const section = lineItemsSectionRef.current;
    if (section && typeof section.scrollIntoView === 'function') {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleCategoryClick = useCallback((category) => openDrillDown('category', category), [openDrillDown]);
  const handleProjectClick = useCallback((project) => openDrillDown('project', project), [openDrillDown]);
  const handleProductClick = useCallback((product) => openDrillDown('product', product), [openDrillDown]);

  // Custom label for displaying the forecast confidence text based on time range
  const getForecastLabel = () => {
    if (!processedData || !processedData.summary) return '';

    // For 1-month view, add a special note
    if (timeRange === '1month') {
      return 'Forecast based on available data trends (may use data outside current view)';
    }

    return processedData.summary.confidenceText || '';
  };

  // Download full billing data as CSV
  const downloadBillingCSV = () => {
    if (filteredLineItems.length === 0) return;
    downloadTextFile(
      `${toFileSlug(accountName)}_billing_${timeRange}_${todayStamp()}.csv`,
      toCsv(filteredLineItems)
    );
  };

  // Where the numbers come from and how fresh they are
  let statusText = '';
  if (statusMessage) {
    statusText = statusMessage;
  } else if (cacheStatus.isCached) {
    statusText = `Billing data last updated ${cacheStatus.formattedDate}`;
  }

  // How every amount on the page was converted; empty for USD
  const rateNote = describeRate();
  const rangeLabel = getTimeRangeLabel(timeRange);
  const hasData = Boolean(processedData);
  const isInitialLoad = isLoading && !hasData;
  const totalInvoices = loadProgress && loadProgress.total;

  return (
    <div className="dashboard">
      <header className="app-header">
        <div className="app-header-inner">
          <div className="header-left">
            <div className="logo-box">
              {customLogo ? (
                <img src={customLogo} alt="" className="logo-img" />
              ) : (
                <div className="logo-placeholder" aria-hidden="true">DO</div>
              )}
            </div>
            <div className="header-title">
              <h1><span className="title-prefix">DigitalOcean </span>FinOps Dashboard</h1>
              <span className="account-badge">{accountName}</span>
            </div>
          </div>
          <div className="header-right">
            {headerActions}
            <CurrencyMenu invoices={allInvoices} />
            <ThemeToggle />
            <button type="button" className="btn-secondary btn-icon settings-btn" onClick={() => setShowSettings(true)} aria-label="Settings" title="Settings">
              <Icon name="settings" />
            </button>
            <button type="button" className="btn-secondary logout-btn" onClick={onLogout} title={userEmail ? `Signed in as ${userEmail}` : undefined}>
              <Icon name="logout" />
              <span className="btn-label">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container">
        <div className="filter-bar">
          <div className="segmented" role="group" aria-label="Time range">
            {TIME_RANGES.map((range) => (
              <button
                key={range.value}
                type="button"
                className={range.value === timeRange ? 'segment is-active' : 'segment'}
                aria-pressed={range.value === timeRange}
                aria-label={range.label}
                title={range.label}
                data-range={range.value}
                onClick={() => { if (range.value !== timeRange) onTimeRangeChange(range.value); }}
              >
                {range.shortLabel}
              </button>
            ))}
          </div>

          {statusText && (
            <p className="data-status" role="status">
              <span className="data-status-dot" aria-hidden="true"></span>
              {statusText}
            </p>
          )}

          <div className="controls">
            <button type="button" onClick={onRefresh} disabled={isLoading}>
              <Icon name="refresh" />
              {isLoading && hasData ? 'Refreshing...' : 'Refresh Data'}
            </button>
            <button type="button" className="btn-secondary" onClick={downloadBillingCSV} disabled={isLoading || filteredLineItems.length === 0}>
              <Icon name="download" />
              Download CSV
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowReport(true)}>
              <Icon name="report" />
              Generate Report
            </button>
            <button type="button" className="btn-secondary" onClick={onClearCache} disabled={isLoading || !cacheStatus.isCached}>
              Clear Cache
            </button>
          </div>
        </div>

        <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} onLogoChange={setCustomLogo} />

        {/* Keyed by account so a selection never carries over to another account's invoices */}
        <GenerateReportModal
          key={accountName}
          isOpen={showReport}
          onClose={() => setShowReport(false)}
          invoices={allInvoices}
          apiToken={apiToken}
          accountName={accountName}
        />

        {warning && (
          <div className="alert alert-warning" role="status">
            <span>{warning}</span>
          </div>
        )}

        {isInitialLoad && (
          <>
            <div className="loading-indicator" role="status" aria-live="polite">
              <div className="loading-spinner" aria-hidden="true"></div> Loading {accountName}'s DigitalOcean billing data...
              {totalInvoices > 0 && (
                <div className="loading-progress">
                  <progress value={loadProgress.done} max={totalInvoices} aria-label="Invoices loaded"></progress>
                  <span>{loadProgress.done} of {totalInvoices} invoices</span>
                </div>
              )}
            </div>
            <LoadingSkeleton />
          </>
        )}

        {error && !isLoading && (
          <div className="alert" role="alert">
            <span>{error}</span>
            <span className="alert-actions">
              <button type="button" className="btn-secondary btn-small" onClick={onRefresh}>Try again</button>
              {onDismissError && (
                <button type="button" className="close-btn" onClick={onDismissError} aria-label="Dismiss error">×</button>
              )}
            </span>
          </div>
        )}

        {hasData && (
          <div className={isLoading ? 'dashboard-content is-refreshing' : 'dashboard-content'} aria-busy={isLoading}>
            <div className="time-range-info">
              Showing <strong>{rangeLabel}</strong> · {filteredLineItems.length.toLocaleString()} of {detailedLineItems.length.toLocaleString()} line items
              {rateNote && <p className="rate-note">{rateNote}</p>}
            </div>

            {filteredLineItems.length === 0 && detailedLineItems.length > 0 && (
              <div className="empty-state">
                <p><strong>No line items in {rangeLabel}.</strong></p>
                <p>This account has no billing activity in the selected period. Choose a longer time range to see earlier invoices.</p>
                <button type="button" className="btn-secondary" onClick={() => onTimeRangeChange('all')}>Show all time</button>
              </div>
            )}

            <SummaryCards
              summary={processedData.summary}
              accountName={accountName}
              monthlyData={processedData.monthlyData}
            />

            <section className="chart-container">
              <div className="chart-header">
                <h3 className="chart-title">Monthly spend trend</h3>
                <label className="inline-checkbox">
                  <input
                    type="checkbox"
                    checked={showForecast}
                    onChange={() => setShowForecast(!showForecast)}
                  />
                  Show Next Month Forecast
                </label>
              </div>
              <div className="chart">
                <MonthlyChart
                  data={processedData.monthlyData}
                  showForecast={showForecast}
                  forecastAmount={processedData.summary.forecastAmount}
                />
              </div>
              {showForecast && (
                <div className="chart-footnote">
                  <ul className="chart-key">
                    <li><span className="key-point key-actual" aria-hidden="true"></span>Actual</li>
                    <li><span className="key-point key-forecast" aria-hidden="true"></span>Forecast</li>
                  </ul>
                  <span>{getForecastLabel()}</span>
                </div>
              )}
            </section>

            <div className="chart-grid">
              <section className="chart-container">
                <div className="chart-header">
                  <h3 className="chart-title">Spend by category</h3>
                  <span className="chart-subtitle">Select a category to see its line items</span>
                </div>
                <CategoryChart data={processedData.categoryData} onCategorySelect={handleCategoryClick} />
              </section>
              <section className="chart-container">
                <div className="chart-header">
                  <h3 className="chart-title">Spend by project</h3>
                  <DrillDownSelect
                    label="View line items for a project"
                    data={processedData.projectData}
                    onSelect={handleProjectClick}
                  />
                </div>
                {detailedLineItems.length === 0 && (
                  <div className="chart-hint">
                    Note: Loading detailed project data from DigitalOcean API...
                  </div>
                )}
                <ProjectChart data={processedData.projectData} onProjectSelect={handleProjectClick} />
              </section>
            </div>

            {/* Detailed Line Items (Interactive) */}
            <section className="chart-container" ref={lineItemsSectionRef}>
              <div className="chart-header">
                <h3 className="chart-title">
                  {drillDown ? `Line items: ${drillDown.value}` : 'Spend by product'}
                </h3>
              </div>
              {drillDown ? (
                <LineItemExplorer
                  detailedLineItems={detailedLineItems}
                  selectedCategory={drillDown.value}
                  selectedProject={drillDown.type === 'project' ? drillDown.value : undefined}
                  drillDownType={drillDown.type}
                  onBack={() => setDrillDown(null)}
                  timeRange={timeRange}
                  accountName={accountName}
                />
              ) : (
                <DetailedLineItemsChart
                  detailedLineItems={filteredLineItems} // Use filtered line items here
                  timeRange={timeRange}
                  onCategoryClick={handleProductClick}
                  accountName={accountName}
                />
              )}
            </section>

            <section className="chart-container">
              <div className="chart-header">
                <h3 className="chart-title">Net spend by product, including discounts</h3>
              </div>
              <ProductChart data={processedData.productData} />
            </section>

            <InvoiceTable
              invoices={allInvoices}
              timeRange={timeRange}
              apiToken={apiToken}
              accountName={accountName}
            />
          </div>
        )}

        {!hasData && !isLoading && !error && (
          <div className="empty-state">
            <p><strong>No invoices found for {accountName}.</strong></p>
            <p>
              DigitalOcean has not issued an invoice for this account yet, or the API token belongs to a different team.
              Invoices appear here after the first billing cycle.
            </p>
            <button type="button" onClick={onRefresh}>Check again</button>
          </div>
        )}
      </main>
    </div>
  );
};
